import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import secureStorage from '../services/secureStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to check if token is expired (JWT tokens have exp claim)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000;
    return Date.now() >= expiry;
  } catch (e) {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(() => secureStorage.getTenant());
  const [token, setToken] = useState(() => {
    const storedToken = secureStorage.getToken();
    if (storedToken && !isTokenExpired(storedToken)) {
      return storedToken;
    }
    if (storedToken) {
      secureStorage.removeToken();
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = secureStorage.getToken();
      const storedTenant = secureStorage.getTenant();
      
      if (storedToken && isTokenExpired(storedToken)) {
        secureStorage.clearAll();
        setToken(null);
        setUser(null);
        setTenant(null);
        setLoading(false);
        return;
      }
      
      if (storedToken) {
        try {
          api.setToken(storedToken);
          const userData = await api.getMe();
          setUser(userData);
          setToken(storedToken);
          if (storedTenant) {
            setTenant(storedTenant);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          secureStorage.clearAll();
          setToken(null);
          setUser(null);
          setTenant(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      const { access_token, user: userData, tenant: tenantData } = response;
      
      secureStorage.setToken(access_token);
      secureStorage.setUser(userData);
      if (tenantData) {
        secureStorage.setTenant(tenantData);
        setTenant(tenantData);
      }
      
      api.setToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData, tenant: tenantData };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const register = async (registrationData) => {
    try {
      const response = await api.registerSaaS(registrationData);
      const { access_token, user: userData, tenant: tenantData, needs_subscription } = response;
      
      secureStorage.setToken(access_token);
      secureStorage.setUser(userData);
      if (tenantData) {
        secureStorage.setTenant(tenantData);
        setTenant(tenantData);
      }
      
      api.setToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData, tenant: tenantData, needs_subscription };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const registerLegacy = async (email, password, name, role = 'engineer') => {
    try {
      const response = await api.register(email, password, name, role);
      const { access_token, user: userData } = response;
      
      secureStorage.setToken(access_token);
      secureStorage.setUser(userData);
      api.setToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const logout = () => {
    secureStorage.clearAll();
    api.setToken(null);
    setToken(null);
    setUser(null);
    setTenant(null);
  };

  const updateTenant = (tenantData) => {
    if (tenantData) {
      secureStorage.setTenant(tenantData);
      setTenant(tenantData);
    }
  };

  const hasActiveSubscription = () => {
    return tenant?.subscription_status === 'active';
  };

  const getSubscriptionPlan = () => {
    return tenant?.subscription_plan || null;
  };

  const value = {
    user, tenant, token, loading,
    isAuthenticated: !!token && !!user,
    hasActiveSubscription: hasActiveSubscription(),
    subscriptionPlan: getSubscriptionPlan(),
    login, register, registerLegacy, logout, updateTenant
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
